import {
  Box,
  ClickAwayListener,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ResourcesHeaderItem } from '../models/ResourcesHeaderItem';
import { useWindow } from '../hooks/useWindow';

interface ResourcesHeaderProps {
  categories: ResourcesHeaderItem[];
  selectedCategoryId?: number | null;
  onCategoryClicked: (index: number) => void;
  isResizable?: boolean;
}

export const ResourcesHeader = ({
  categories,
  selectedCategoryId,
  onCategoryClicked,
  isResizable = true,
}: ResourcesHeaderProps) => {
  const outsideRef = useRef<HTMLElement>(null);
  const insideRef = useRef<HTMLElement>(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [menuItems, setMenuItems] = useState<HTMLDivElement[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const selectedCategoryStyle = {
    fontWeight: 600,
    color: '#307fe2',
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleResize = useCallback(() => {
    if (
      insideRef.current === null ||
      outsideRef.current === null ||
      !isResizable
    ) {
      return;
    }
    const clientWidth = outsideRef.current.clientWidth + 170;
    const children = insideRef.current.querySelectorAll('div');
    [...children].forEach((elm) => {
      elm.style.display = 'block';
    });
    const matches = [...children].filter((child) => {
      const rect = child.getBoundingClientRect();
      return rect.right > clientWidth && rect.left > clientWidth;
    });
    if (matches.length === 0) {
      setAnchorEl(null);
    }
    setMenuItems(matches);
    matches.forEach((item) => {
      item.style.display = 'none';
    });
  }, [isResizable]);

  useWindow(handleResize);

  useEffect(() => {
    handleResize();
  }, [handleResize, categories]);

  useEffect(() => {
    if (selectedCategoryId) {
      const selectedIndex = categories.findIndex(
        (item) => item.id === selectedCategoryId,
      );
      setSelectedCategoryIndex(selectedIndex);
    } else {
      setSelectedCategoryIndex(0);
    }
  }, [selectedCategoryId, categories]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        pt: 12.5,
        pl: 7.5,
        pr: 7.5,
      }}
    >
      <Box ref={outsideRef} sx={{ overflow: 'hidden', pr: 7.5, flex: 1 }}>
        <Box
          ref={insideRef}
          sx={{ display: 'inline-flex', gap: 10, width: '100%' }}
        >
          {categories.map((element, index) => (
            <Box
              key={element.id}
              sx={{
                flex: 1,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
              onClick={() => {
                setSelectedCategoryIndex(index);
                onCategoryClicked(index);
              }}
            >
              <Typography
                variant={'body2'}
                sx={
                  index === selectedCategoryIndex ? selectedCategoryStyle : {}
                }
              >
                {element.name}
              </Typography>
              {index === selectedCategoryIndex && (
                <Divider
                  sx={{
                    borderColor: '#307fe2',
                    pt: 2,
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>
      {menuItems.length > 0 && (
        <Box>
          <div>
            <Box onClick={handleClick}>
              {open ? (
                <ExpandLess
                  style={{
                    cursor: 'pointer',
                  }}
                />
              ) : (
                <ExpandMore
                  style={{
                    cursor: 'pointer',
                  }}
                />
              )}
            </Box>
            <Popper
              open={open}
              anchorEl={anchorEl}
              placement={'bottom-end'}
              modifiers={[
                {
                  name: 'offset',
                  enabled: true,
                  options: {
                    offset: [25, 0],
                  },
                },
              ]}
              sx={{
                zIndex: 1,
              }}
            >
              <Paper
                sx={{
                  overflow: 'visible',
                  width: '320px',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  boxShadow:
                    '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                  borderRadius: 2.5,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 30,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                  },
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList>
                    {menuItems.map((item, menuItemIndex) => (
                      <MenuItem
                        key={item.innerText}
                        sx={{
                          pt: 2.5,
                          pb: 2.5,
                        }}
                        onClick={() => {
                          const index =
                            categories.length -
                            menuItems.length +
                            menuItemIndex;
                          setSelectedCategoryIndex(index);
                          onCategoryClicked(index);
                          handleClose();
                        }}
                      >
                        <Typography
                          variant={'body2'}
                          style={{
                            fontWeight:
                              menuItems.length -
                                (categories.length - selectedCategoryIndex) ===
                              menuItemIndex
                                ? 600
                                : undefined,
                          }}
                        >
                          {item.innerText}
                        </Typography>
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Popper>
          </div>
        </Box>
      )}
    </Box>
  );
};
